// Order lifecycle — extended from the BRD's original 8 stages to 10, to
// carry the revised approval flow:
//
//   MR raises order on behalf of pharmacy
//     -> Pharmacy confirms via OTP
//     -> Sales Supervisor validates (approve / reject / comment cycle)
//     -> Telecaller validates
//     -> ...original warehouse/dispatch/billing tail, unchanged
//
// The two new gates (OTP Confirmation, Supervisor Approval Pending) sit at
// the front. Everything from Telecaller Validation onward keeps its original
// BRD Module 4 ordering, so nothing downstream had to be renumbered.
export const STAGES = [
  'Order Received',
  'Pharmacy OTP Confirmation',
  'Supervisor Approval Pending',
  'Telecaller Validation',
  'Stock Allocation',
  'Warehouse Processing',
  'Dispatch Pending',
  'Dispatched',
  'Invoice Generated',
  'Delivered',
];

// Named indices, so code reads intent instead of magic numbers and stays
// correct if stages are ever reordered again.
export const STAGE = {
  ORDER_RECEIVED: 'Order Received',
  OTP_CONFIRMATION: 'Pharmacy OTP Confirmation',
  SUPERVISOR_APPROVAL: 'Supervisor Approval Pending',
  TELECALLER_VALIDATION: 'Telecaller Validation',
  STOCK_ALLOCATION: 'Stock Allocation',
  WAREHOUSE_PROCESSING: 'Warehouse Processing',
  DISPATCH_PENDING: 'Dispatch Pending',
  DISPATCHED: 'Dispatched',
  INVOICE_GENERATED: 'Invoice Generated',
  DELIVERED: 'Delivered',
};

// Lateral states outside the main sequence — not extra stages, genuine
// alternate outcomes an order can sit in instead of progressing.
export const LATERAL_STATES = {
  CANCELLED: 'Cancelled',
  RETURNED: 'Returned for Modification', // the comment/bounce-back mechanic
};

export function isLateral(stage) {
  return !STAGES.includes(stage);
}

// Every state transition carries a real timestamp — the actual TAT source.
export function tatSince(order, nowIso = new Date().toISOString()) {
  const history = order.statusHistory;
  if (!history || !history.length) return null;
  const last = history[history.length - 1];
  const ms = new Date(nowIso) - new Date(last.timestamp);
  return Math.max(0, Math.round(ms / (1000 * 60 * 60))); // hours
}

export function formatTat(hours) {
  if (hours === null) return '—';
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// Comment chain: reuses the SAME order record, no new order is created.
export function addComment(order, { from, text }) {
  order.comments = order.comments || [];
  order.comments.push({ from, text, timestamp: new Date().toISOString() });
}

export function totalOrderValue(lineItems) {
  return Math.round(lineItems.reduce((sum, li) => sum + li.lineValue, 0) * 100) / 100;
}

// Fulfilment status per line item, set by the Telecaller (point 5).
export const FULFILMENT = {
  PENDING: 'Pending',
  FULL: 'Fulfilled',
  PARTIAL: 'Partially Fulfilled',
  UNAVAILABLE: 'Unavailable',
};

// Order-level fulfilment % — quantity-weighted (not a simple count average),
// so a big line that's half-fulfilled matters more than a small line that's
// fully fulfilled. `qtyField` lets the same helper serve both Telecaller's
// fulfilledQty and Warehouse's independent warehouseAllocatedQty.
export function orderFulfilmentPct(order, qtyField = 'fulfilledQty') {
  const totalOrdered = order.lineItems.reduce((s, li) => s + li.quantity, 0)
  if (!totalOrdered) return null
  const anySet = order.lineItems.some(li => li[qtyField] !== null && li[qtyField] !== undefined)
  if (!anySet) return null
  const totalFulfilled = order.lineItems.reduce((s, li) => s + (li[qtyField] ?? li.quantity), 0)
  return Math.round((totalFulfilled / totalOrdered) * 100)
}
